import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu, X, Sparkles, Users, MessageCircle, MessageSquare, Newspaper,
  User as UserIcon, Headset, Settings, LogOut, Coins, Trophy,
} from "lucide-react";
import { logout as authLogout, authAxios } from "../utils/auth";
import { toast } from "sonner";

/**
 * Hamburger main-menu — the /dashboard mobile menu, promoted to a
 * reusable component that works on every viewport. Pass an `exclude`
 * path (e.g. "/news") to hide that entry from the dropdown when the
 * user is already on that page.
 *
 * On mobile a highlighted "Top Up Coins" block sits at the very top
 * showing the user's live balance and linking to /plans. On desktop
 * that block is hidden (the page-header CoinBalancePill covers it).
 */
const EROS_LOGO_URL = "https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png";

const MENU_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: Sparkles },
  { to: "/eroscoach", label: "Agent Eros Advice", iconImg: EROS_LOGO_URL },
  { to: "/relationships", label: "Relationships", icon: Users },
  { to: "/forum", label: "Support Forum", icon: MessageSquare },
  { to: "/news", label: "News & Updates", icon: Newspaper },
  { to: "/affiliate", label: "Affiliate Dashboard", icon: Trophy },
  { to: "/profile", label: "My Account", icon: UserIcon },
  { to: "/profile?tab=support", label: "Contact Us", icon: Headset },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function MainMenu({ exclude = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const menuRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Fetch coin balance on mount (for the mobile highlighted block)
  useEffect(() => {
    let alive = true;
    authAxios.get("/credits/balance")
      .then((r) => { if (alive) setBalance(r?.data?.balance ?? null); })
      .catch(() => { if (alive) setBalance(null); });
    return () => { alive = false; };
  }, []);

  const items = MENU_ITEMS.filter((i) => {
    if (!exclude) return true;
    const itemPath = i.to.split("?")[0];
    return itemPath !== exclude;
  });

  const handleLogout = async () => {
    setOpen(false);
    try {
      await authLogout();
      toast.success("Until we meet again...");
      navigate("/");
    } catch {
      toast.error("Logout failed");
    }
  };

  return (
    <div ref={menuRef} className="relative" data-testid="main-menu">
      <button
        onClick={() => setOpen((o) => !o)}
        className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        data-testid="main-menu-toggle"
        aria-label="Open navigation menu"
        title="Menu"
      >
        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {open && (
        <div
          className="fixed inset-x-0 top-0 z-[100] bg-divine-black-light border-b border-white/10 shadow-2xl sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:mt-1 sm:w-56 sm:rounded-xl sm:border sm:overflow-hidden"
          data-testid="main-menu-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sm:px-3 sm:py-2">
            <span className="text-sm sm:text-xs font-semibold text-white/40 uppercase tracking-wider">Navigation</span>
            <button
              onClick={() => setOpen(false)}
              className="sm:hidden p-1 -mr-1 text-white/60 hover:text-white"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="py-2 sm:py-1 max-h-[80vh] overflow-y-auto">
            {/* Mobile-only highlighted "Top Up Coins" button — matches
                the Dashboard mobile menu coin block styling. */}
            <div
              onClick={() => {
                setOpen(false);
                navigate("/plans?tab=topup");
              }}
              className="sm:hidden mx-4 mb-2 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg cursor-pointer hover:bg-amber-500/20 transition-colors"
              data-testid="main-menu-coin-cta"
            >
              <Coins className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 font-medium">
                {balance !== null ? balance.toLocaleString() : "—"} coins. Add more
              </span>
            </div>

            {items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-base text-white/70 hover:text-white hover:bg-white/5 transition-colors sm:gap-2.5 sm:px-3 sm:py-2 sm:text-sm"
                  data-testid={`main-menu-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.iconImg ? (
                    <img
                      src={item.iconImg}
                      alt=""
                      className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0 object-contain"
                    />
                  ) : (
                    <Icon className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
                  )}
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <div className="border-t border-white/5 my-1" />
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-base text-divine-red hover:bg-divine-red/10 transition-colors sm:gap-2.5 sm:px-3 sm:py-2 sm:text-sm"
              data-testid="main-menu-logout"
            >
              <LogOut className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>Logout</span>
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}
